import { UsePDFInstance } from "@react-pdf/renderer";
import { now } from "moment";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import BackEndService from "../api/BackEndService";
import DeficiencyService from "../api/DeficiencyService";
import { generateUUIDFromID } from "../services/UUIDGeneratorService";
import { RootState } from "../store";
import {
  Attachment,
  EmailProps,
  creatingDocumentationActions,
} from "../store/documentation-slice";
import { ImageProps } from "../types/image";

export const useDocumentation = () => {
  const dispatch = useDispatch();
  const [requestLoading, setLoading] = useState(false);
  const [noDocument, setNoDocument] = useState(false);

  const documentation = useSelector((state: RootState) => state.documentation);

  const generateFormData = (instance: UsePDFInstance) => {
    if (!instance.url || !instance.blob) return;

    const formData = new FormData();
    formData.append("uuid", generateUUIDFromID(now().toString()));
    formData.append("name", instance.url);
    formData.append("file", instance.blob, "Leistungsnachweis.pdf");

    return formData;
  };

  const savePDF = async (instance: UsePDFInstance, attachedTo: Attachment) => {
    const formData = generateFormData(instance);

    try {
      const image: any = await BackEndService.UploadImage(formData);
      const document: any = await BackEndService.AddDocument({
        documentType: "receipt",
        fileName: "Leistungsnachweis.pdf",
        mimeType: "application/pdf",
        fileSize: instance.blob?.size,
        isInternal: true,
        url: instance.url,
        media: image.uuid,
        attachedTo: attachedTo,
      });
      dispatch(
        creatingDocumentationActions.setExistingReceiptId(document.data.id)
      );
      dispatch(creatingDocumentationActions.setExistingReceipt(true));
    } catch (error) {
      throw error;
    }
  };

  const updatePDF = async (instance: UsePDFInstance) => {
    const formData = generateFormData(instance);

    try {
      const image: any = await BackEndService.UploadImage(formData);
      const document: any = await BackEndService.UpdateDocument(
        documentation.existingReceiptId,
        {
          fileName: "Leistungsnachweis.pdf",
          fileSize: instance.blob?.size,
          isInternal: true,
          url: image.name,
        }
      );
      dispatch(
        creatingDocumentationActions.setExistingReceiptId(document.data.id)
      );
      dispatch(creatingDocumentationActions.setExistingReceipt(true));
    } catch (error) {
      throw error;
    }
  };

  const generatePDF = () => {};

  const convertDocumentsToFiles = async (documents: any) => {
    return await Promise.all(
      documents.map(async (doc: any) => {
        const response = await fetch(doc.url);
        const blob = await response.blob();
        return new File([blob], doc.fileName, { type: doc.mimeType });
      })
    );
  };

  const sendPDFviaEmail = async (
    deficiencyId: string,
    pdf: Blob,
    emails: EmailProps[],
    subject: string,
    attachments?: ImageProps[]
  ) => {
    if (!pdf) {
      console.error("PDF generation failed.");
      return;
    }

    setLoading(true);
    try {
      const emailPromises = emails.map(async (email) => {
        const pdfClone = pdf.slice(0, pdf.size, pdf.type);

        const formData = new FormData();
        formData.append("to_email", email.email);
        formData.append("subject", subject);
        formData.append("pdf", pdfClone, "Leistungsnachweis.pdf");

        if (attachments) {
          const attachmentFiles = await convertDocumentsToFiles(attachments);

          attachmentFiles.forEach((file, index) => {
            formData.append(`attachments[${index}]`, file);
          });
        }

        await DeficiencyService.SendDeficiencyPDFViaEmail(
          deficiencyId,
          formData
        );
      });

      await Promise.all(emailPromises);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentation = (url: string | undefined) => {
    if (!url) {
      setNoDocument(true);
      return;
    }

    if (setLoading) setLoading(true);

    window.open(url);

    if (setLoading) setLoading(false);
  };

  return {
    fetchDocumentation,
    generatePDF,
    sendPDFviaEmail,
    savePDF,
    updatePDF,
    requestLoading,
    noDocument,
    setNoDocument,
  };
};
